import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, Typography } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import { useEffect, useMemo, useRef, useState } from "react";
import useApiHook from "../hooks/useApiHook";
import cancelButtonSx from "../styles/cancelButtonSx";

const LONG_FILE_THRESHOLD_SECONDS = 60 * 60;
const PROLONGED_WAIT_SECONDS = 180;
const POLL_INTERVAL_MS = 3000;

const TranscriptionEstimateDialog = ({ open, fileId, audioDurationSeconds, onCompleted, onClose, onConfirmError }) => {
    const { makeRequest } = useApiHook();
    const startedAtRef = useRef(null);
    const pollingIntervalRef = useRef(null);
    const onCloseRef = useRef(onClose);
    const onCompletedRef = useRef(onCompleted);
    const [elapsedSeconds, setElapsedSeconds] = useState(0);
    const [hasError, setHasError] = useState(false);
    const isLongFile = useMemo(
        () => Boolean(audioDurationSeconds && audioDurationSeconds >= LONG_FILE_THRESHOLD_SECONDS),
        [audioDurationSeconds]
    );

    const modalCopy = useMemo(() => {
        if (isLongFile) {
            if (elapsedSeconds >= PROLONGED_WAIT_SECONDS) {
                return {
                    title: 'Elaborazione ancora in corso',
                    message: 'Il processo e attivo. Ti avviseremo al completamento.'
                };
            }

            return {
                title: 'File di lunga durata',
                message: 'L elaborazione potrebbe richiedere alcuni minuti.'
            };
        }

        if (elapsedSeconds >= PROLONGED_WAIT_SECONDS) {
            return {
                title: 'Elaborazione ancora in corso',
                message: 'Il processo e attivo. Ti avviseremo al completamento.'
            };
        }

        return {
            title: 'Trascrizione in corso',
            message: 'Stiamo elaborando il file.'
        };
    }, [elapsedSeconds, isLongFile]);

    useEffect(() => {
        onCloseRef.current = onClose;
        onCompletedRef.current = onCompleted;
    }, [onClose, onCompleted]);

    useEffect(() => {
        if (!open || !fileId) {
            return undefined;
        }

        startedAtRef.current = Date.now();
        setElapsedSeconds(0);
        setHasError(false);

        const pollFileStatus = async () => {
            const result = await makeRequest(`/file/${fileId}/details`);

            if (result?.state !== 'success') {
                return;
            }

            if (result.data?.status === 'done') {
                if (onCompletedRef.current) {
                    onCompletedRef.current(result.data);
                }
                if (onCloseRef.current) {
                    onCloseRef.current();
                }
                return;
            }

            if (result.data?.status === 'error') {
                setHasError(true);
                if (pollingIntervalRef.current) {
                    clearInterval(pollingIntervalRef.current);
                    pollingIntervalRef.current = null;
                }
            }
        };

        pollFileStatus();
        pollingIntervalRef.current = setInterval(pollFileStatus, POLL_INTERVAL_MS);
        const elapsedInterval = setInterval(() => {
            setElapsedSeconds(Math.floor((Date.now() - startedAtRef.current) / 1000));
        }, 1000);

        return () => {
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
            }

            clearInterval(elapsedInterval);
        };
    }, [fileId, makeRequest, open]);

    return (
        <Dialog
            open={open}
            onClose={() => onCloseRef.current && onCloseRef.current()}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle sx={{ pb: 0 }}>
                <Stack direction="row" justifyContent="flex-end">
                    <IconButton
                        aria-label="chiudi"
                        onClick={() => onCloseRef.current && onCloseRef.current()}
                        size="small"
                    >
                        <CloseIcon />
                    </IconButton>
                </Stack>
            </DialogTitle>
            <DialogContent>
                {!hasError && (
                    <Stack spacing={3} py={1} alignItems="center">
                        <CircularProgress size={36} thickness={4.5} />
                        <Stack spacing={1} alignItems="center">
                            <Typography variant="h5" fontWeight="bold" textAlign="center">
                                {modalCopy.title}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" textAlign="center">
                                {modalCopy.message}
                            </Typography>
                        </Stack>

                        <Typography variant="body2" textAlign="center" color="text.secondary">
                            Puoi chiudere questa finestra in qualsiasi momento.
                        </Typography>
                        <Typography variant="body2" textAlign="center" color="text.secondary">
                            (Riceverai una notifica al termine)
                        </Typography>
                    </Stack>
                )}

                {hasError && (
                    <Stack spacing={2} py={1} alignItems="center">
                        <Typography variant="h5" fontWeight="bold" textAlign="center">
                            Trascrizione non completata
                        </Typography>
                        <Typography variant="body2" textAlign="center">
                            Non e stato possibile completare la trascrizione in questo momento.
                        </Typography>
                        <Typography variant="body2" color="text.secondary" textAlign="center">
                            Puoi riprovare tra poco.
                        </Typography>
                    </Stack>
                )}
            </DialogContent>
            {hasError && (
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        variant="contained"
                        onClick={() => {
                            if (onCloseRef.current) {
                                onCloseRef.current();
                            }
                            if (onConfirmError) {
                                onConfirmError();
                            }
                        }}
                    >
                        OK
                    </Button>
                    <Button variant="outlined" onClick={() => onCloseRef.current && onCloseRef.current()} sx={cancelButtonSx}>
                        Annulla
                    </Button>
                </DialogActions>
            )}
        </Dialog>
    );
};

export default TranscriptionEstimateDialog;
